// src/components/Navbar.jsx
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Box,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  Paper
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/formlogo4.svg';
import { useAuth } from '../../context/AuthContext';
import { useDispatch, useSelector } from 'react-redux';
import { changeAppToken } from '../../redux/slices/applicationTokenSlice';
import { changeLoginData } from '../../redux/slices/loginSlice';
import '../../styles/Navbar.css';

const Navbar = ({ onToggleSidebar }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [isSidebarButtonHovered, setIsSidebarButtonHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const reduxDispatch = useDispatch();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isFormUser, setIsFormUser] = useState(false);
  
  // Redux store'dan kullanıcı bilgilerini al
  const userData = useSelector((state) => state.loginData.loginData);

  // Form kullanıcısı kontrolü
  useEffect(() => {
    const formUserToken = localStorage.getItem('formUserToken');
    const formUserData = localStorage.getItem('formUserData');
    
    if (formUserToken && formUserData) {
      try {
        const parsedUserData = JSON.parse(formUserData);
        if (parsedUserData && parsedUserData.id) {
          setIsFormUser(true);
        }
      } catch (error) {
        console.error('Error parsing stored form user data:', error);
      }
    }
  }, []);

  // Form kullanıcısı giriş yapmışsa navbar'ı gösterme
  if (isFormUser) {
    return null;
  }

  // Menü açıldığında animasyon için state
  useEffect(() => {
    if (anchorEl) {
      setMenuOpen(true);
    } else {
      setMenuOpen(false);
    }
  }, [anchorEl]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsOpen = (event) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handleLogout = () => {
    // Panel kullanıcısı için localStorage'ı temizle
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('userType');
    
    // Form kullanıcısı için localStorage'ı da temizle (güvenlik için)
    localStorage.removeItem('formUserToken');
    localStorage.removeItem('formUserData');
    localStorage.removeItem('formUserType');
    
    // Auth context'i temizle
    logout();
    
    // Redux store'u temizle
    reduxDispatch(changeAppToken(null));
    reduxDispatch(changeLoginData({}));
    
    // Login sayfasına yönlendir
    navigate("/login");
  };

  const handleNavigate = (path) => {
    navigate(path);
    handleClose();
  };

  // Aktif sayfa kontrolü
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <AppBar position="fixed" className="navbar">
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', height: '100%', px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <IconButton
            edge="start"
            aria-label="menu"
            className={`navbar-button sidebar-toggle ${isSidebarButtonHovered ? 'hovered' : ''}`}
            onClick={onToggleSidebar}
            onMouseEnter={() => setIsSidebarButtonHovered(true)}
            onMouseLeave={() => setIsSidebarButtonHovered(false)}
          >
            <MenuIcon className="menu-icon" />
          </IconButton>

          <Box 
            className="logo-container"
            onClick={() => navigate('/')}
            sx={{ ml: 2 }}
          >
            <img  
              src={logo} 
              alt="Form Icon" 
              className="navbar-logo"
            />
            <Box sx={{ ml: 3 }}>
              <Typography 
                variant="h6" 
                component="div" 
                className="navbar-title"
                sx={{ 
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                {t('form_clicker')}
                <div className="navbar-subtitle">{t('discover_forms_digitally')}</div>
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box 
            className="profile-button"
            onClick={handleMenu}
          >
            <Avatar 
              className="avatar"
              sx={{ 
                bgcolor: '#3498db',
                color: 'white',
              }}
            >
              <PersonIcon />
            </Avatar>
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography className="profile-name">
                  {userData?.username || 'Kullanıcı'}
                </Typography>
                <ArrowDownIcon sx={{ color: '#2c3e50', fontSize: 20 }} />
              </Box>
            )}
          </Box>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          onClick={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          TransitionComponent={Grow}
          TransitionProps={{ timeout: 300 }}
          PaperProps={{
            elevation: 8,
            sx: {
              borderRadius: '12px',
              overflow: 'hidden',
              minWidth: '220px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              '& .MuiMenuItem-root': {
                padding: '10px 16px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(52, 152, 219, 0.08)',
                }
              }
            }
          }}
        >
          {userData && (
            <Box sx={{ px: 2, py: 1.5, background: 'linear-gradient(135deg, #4158D0 0%, #C850C0 100%)', color: 'white' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {userData.username || 'Kullanıcı'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                {userData.username || 'kullanıcı@email.com'}
              </Typography>
            </Box>
          )}
          
          <MenuItem onClick={() => handleNavigate('/profile')} className="menu-item">
            <ListItemIcon>
              <PersonIcon fontSize="small" sx={{ color: '#3498db' }} />
            </ListItemIcon>
            {t('profile')}
          </MenuItem>
          
          <MenuItem onClick={() => handleNavigate('/user-definition')} className="menu-item">
            <ListItemIcon>
              <PersonIcon fontSize="small" sx={{ color: '#3498db' }} />
            </ListItemIcon>
            Kullanıcı Tanımlama
          </MenuItem>
          
   
          
          <Divider sx={{ my: 1 }} />
          
          <MenuItem onClick={handleLogout} className="menu-item" sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" color="error" />
            </ListItemIcon>
            {t('logout')}
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={notificationsAnchor}
          open={Boolean(notificationsAnchor)}
          onClose={handleNotificationsClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          TransitionComponent={Grow}
          TransitionProps={{ timeout: 300 }}
          PaperProps={{
            elevation: 8,
            sx: {
              borderRadius: '12px',
              overflow: 'hidden',
              width: '320px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('notifications')}
            </Typography>
          </Box>
          
          <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
            <MenuItem onClick={handleNotificationsClose} sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('new_form_response')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('new_response_customer_satisfaction')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('2_hours_ago')}
                </Typography>
              </Box>
            </MenuItem>
            
            <MenuItem onClick={handleNotificationsClose} sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('form_shared')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('job_application_form_viewed')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('yesterday')}
                </Typography>
              </Box>
            </MenuItem>
            
            <MenuItem onClick={handleNotificationsClose} sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('system_notification')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('new_features_added')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('3_days_ago')}
                </Typography>
              </Box>
            </MenuItem>
          </Box>
          
          <Box sx={{ p: 1.5, borderTop: '1px solid rgba(0, 0, 0, 0.08)', textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#3498db', 
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={handleNotificationsClose}
            >
              {t('view_all_notifications')}
            </Typography>
          </Box>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;